import { generatedTeamBranding } from "./generated/teamBranding.generated";
import { Team } from "../features/cfbd/types/team";

export type TeamLogoMode = "light" | "dark";

const localAssetStatuses = new Set(["approved", "legacy-approved"]);

// Compatibility for cards that still key the legacy glow override by team name.
// MatchupCard migrates callers to manifest presentation metadata in the next PR.
const noGlowTeamNames = new Set(["iowa", "iowa hawkeyes"]);

export function shouldRemoveLogoGlow(name?: string): boolean {
  return Boolean(name && noGlowTeamNames.has(name.trim().toLowerCase()));
}

export function getTeamBranding(teamId: number) {
  return generatedTeamBranding[teamId];
}

/**
 * Resolve only reviewed local assets, then the API-provided CFBD/ESPN URL.
 * Components render the team abbreviation if every image candidate fails.
 */
export function resolveTeamLogoCandidates(team: Team, mode: TeamLogoMode): string[] {
  const brand = getTeamBranding(team.id);
  const candidates: Array<string | null | undefined> = [];
  if (brand && localAssetStatuses.has(brand.reviewStatus)) {
    candidates.push(brand.assets.svg?.path);
    if (mode === "dark") candidates.push(brand.assets.themedPng?.path);
    candidates.push(brand.assets.basePng?.path);
  }
  candidates.push(...(team.logos ?? []));
  return Array.from(new Set(candidates.filter((candidate): candidate is string => Boolean(candidate))));
}

export function getAdaptiveLogoFilter(
  teamId: number,
  mode: TeamLogoMode,
  backgroundColor?: string
): string | undefined {
  const presentation = getTeamBranding(teamId)?.presentation;
  if (presentation?.outline === "none" && presentation.approvedOverride) return undefined;
  const normalized = backgroundColor?.match(/^#([0-9a-f]{6})$/i)?.[1];
  let useLightEdge = mode === "dark";
  if (normalized) {
    const channels = [0, 2, 4].map((start) => {
      const value = parseInt(normalized.slice(start, start + 2), 16) / 255;
      return value <= 0.04045
        ? value / 12.92
        : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    const luminance =
      0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
    useLightEdge = luminance < 0.34;
  }
  const edge = useLightEdge
    ? "rgba(255,255,255,.94)"
    : "rgba(0,0,0,.76)";
  return "drop-shadow(0 0 0.6px " + edge + ") drop-shadow(0 1px 1.5px " + edge + ")";
}
